/* eslint max-lines: ["error", { "max": 588 }] */
import { GetEmployerDto, OpenEnrollmentPeriodDto } from '@zorro/clients';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { Modal } from '@zorro/design-system/v2/Modal';

import {
  AccountInvitationForm,
  AddEnrollmentRecordForm,
  BulkChangeAorCancellationStatusForm,
  ChangeElectionWindowForm,
  ChangeEmployeeCustomClassCategoryForm,
  ChangeShoppingStatusForm,
  CobraTerminationEmailForm,
  ConfirmEnrollmentForm,
  DeleteEmployeeForm,
  EditEmployeeRoleForm,
  ElectionDeadlineReminderForm,
  ElectionSummaryForm,
  ElectionWindowLaunchForm,
  EmployeeEmailForm,
  EmployeeLeaveOfAbsenceForm,
  FlagAsApplicationSentForm,
  ResetElectionDialog,
  TerminateEmployeeForm,
  UpdateEligibilityForm,
  WaiveCoverageForm,
  WaiverConfirmationForm,
} from '.';
import {
  CreateEmployeeDrawer,
  UploadRosterForm,
} from '../EmployeeManagementActions';
import { ManagePaymentStatusContent } from '../Payments/PaymentStatus/ManagePaymentStatus';
import { getEmployeeNameOrEmployeesCount } from './ActionsMenuUtils';
import { EnrollmentConfirmationForm } from './EmailCommunications/EnrollmentConfirmationForm';
import { RecordForActions } from './EmployeeActionsUtils';
import { EmployeesSubEntityBulkEditForm } from './EmployeeAdministration/EmployeesSubEntityBulkEditForm';
import { AssignEnrollmentForm } from './EnrollmentActions/AssignEnrollmentForm';
import { BulkChangeCarrierRequestStatusForm } from './EnrollmentActions/BulkChangeCarrierRequestStatusForm';
import { LogEnrollmentCallForm } from './EnrollmentActions/LogEnrollmentCallForm';
import { EmployeeActionsOverlays } from './employeeActions.consts';

type ActionsMenuOverlaysProps = {
  employerId?: GetEmployerDto['id'];
  employerWaitingPeriod?: GetEmployerDto['waitingPeriod'];
  associatedEnrollmentTeamId?: string | null;
  associatedEnrollmentTeamName?: string | null;
  enrollmentPeriods?: OpenEnrollmentPeriodDto[];
  enrollmentPeriodId?: string | null;
  recordsForActions: RecordForActions[];
  resetSelectedRecords: () => void;
  overlayId: EmployeeActionsOverlays;
  onClose: () => void;
};

const ActionsMenuOverlays = ({
  employerId,
  employerWaitingPeriod,
  associatedEnrollmentTeamId,
  associatedEnrollmentTeamName,
  enrollmentPeriods,
  enrollmentPeriodId,
  recordsForActions,
  resetSelectedRecords,
  overlayId,
  onClose,
}: ActionsMenuOverlaysProps) => {
  const hasNonWaivedRecords = recordsForActions.some(
    (record) => !record.isOnboardingWaived
  );

  // Custom class category is a period-scoped write, so only records carrying an
  // onboarding period are eligible; the menu item is greyed out unless every
  // selected record has one.
  const recordsWithPeriod = recordsForActions.filter(
    (record): record is RecordForActions & { onboardingPeriodId: string } =>
      Boolean(record.onboardingPeriodId)
  );

  function resetSelectedEmployeesAndClose() {
    resetSelectedRecords();
    onClose();
  }

  return (
    <>
      {employerId && (
        <Drawer
          opened={overlayId === EmployeeActionsOverlays.UPLOAD_ROSTER}
          title="Upload roster"
          onClose={onClose}
        >
          <UploadRosterForm employerId={employerId} onSuccess={onClose} />
        </Drawer>
      )}

      {employerId && employerWaitingPeriod && enrollmentPeriods && (
        <CreateEmployeeDrawer
          isOpen={overlayId === EmployeeActionsOverlays.ADD_EMPLOYEE}
          onClose={onClose}
          employerId={employerId}
          employerWaitingPeriod={employerWaitingPeriod}
          enrollmentPeriods={enrollmentPeriods}
        />
      )}

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.UPDATE_ELIGIBILITY}
        title="Update eligibility"
        onClose={onClose}
      >
        <UpdateEligibilityForm
          recordsForActions={recordsForActions}
          onSuccess={onClose}
        />
      </Drawer>

      {employerId && (
        <Drawer
          opened={overlayId === EmployeeActionsOverlays.ADD_ENROLLMENT_RECORD}
          title="Add enrollment record"
          onClose={onClose}
        >
          <AddEnrollmentRecordForm
            recordsForActions={recordsForActions}
            employerId={employerId}
            onSuccess={onClose}
          />
        </Drawer>
      )}

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.ASSIGN_ENROLLMENT}
        title="Assign enrollment"
        onClose={onClose}
      >
        <AssignEnrollmentForm
          onClose={onClose}
          employerId={employerId}
          associatedEnrollmentTeamId={associatedEnrollmentTeamId}
          associatedEnrollmentTeamName={associatedEnrollmentTeamName}
          recordsForActions={recordsForActions}
        />
      </Drawer>

      <Drawer
        opened={
          overlayId === EmployeeActionsOverlays.CHANGE_AOR_CANCELLATION_STATUS
        }
        onClose={onClose}
        title={EmployeeActionsOverlays.CHANGE_AOR_CANCELLATION_STATUS}
      >
        <BulkChangeAorCancellationStatusForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={
          overlayId === EmployeeActionsOverlays.CHANGE_CARRIER_REQUEST_STATUS
        }
        onClose={onClose}
        title={EmployeeActionsOverlays.CHANGE_CARRIER_REQUEST_STATUS}
      >
        <BulkChangeCarrierRequestStatusForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Drawer>

      {enrollmentPeriodId && (
        <Drawer
          opened={overlayId === EmployeeActionsOverlays.CHANGE_ELECTION_WINDOW}
          title="Change election window"
          onClose={onClose}
        >
          <ChangeElectionWindowForm
            recordsForActions={recordsForActions}
            planYearId={enrollmentPeriodId}
            onSuccess={onClose}
          />
        </Drawer>
      )}

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.CHANGE_EMPLOYEE_ROLE}
        onClose={onClose}
        title={EmployeeActionsOverlays.CHANGE_EMPLOYEE_ROLE}
      >
        <EditEmployeeRoleForm
          recordsForActions={recordsForActions}
          onSuccess={onClose}
        />
      </Drawer>

      {employerId && (
        <Drawer
          opened={
            overlayId === EmployeeActionsOverlays.CHANGE_EMPLOYEE_CUSTOM_CLASS
          }
          onClose={onClose}
          title={EmployeeActionsOverlays.CHANGE_EMPLOYEE_CUSTOM_CLASS}
        >
          <ChangeEmployeeCustomClassCategoryForm
            recordsForActions={recordsWithPeriod}
            employerId={employerId}
            onClose={onClose}
            onSuccess={onClose}
          />
        </Drawer>
      )}

      {employerId && (
        <Drawer
          opened={
            overlayId === EmployeeActionsOverlays.CHANGE_EMPLOYEE_SUB_ENTITY
          }
          onClose={onClose}
          title={EmployeeActionsOverlays.CHANGE_EMPLOYEE_SUB_ENTITY}
        >
          <EmployeesSubEntityBulkEditForm
            recordsForActions={recordsForActions}
            employerId={employerId}
            onClose={onClose}
            onSuccess={onClose}
          />
        </Drawer>
      )}

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.TERMINATE_EMPLOYEE}
        onClose={onClose}
        title={EmployeeActionsOverlays.TERMINATE_EMPLOYEE}
      >
        <TerminateEmployeeForm
          recordsForActions={recordsForActions}
          onSuccess={resetSelectedEmployeesAndClose}
          onClose={onClose}
        />
      </Drawer>

      <Drawer
        opened={
          overlayId === EmployeeActionsOverlays.SINGLE_SET_LEAVE_OF_ABSENCE
        }
        onClose={onClose}
        title={EmployeeActionsOverlays.SINGLE_SET_LEAVE_OF_ABSENCE}
      >
        <EmployeeLeaveOfAbsenceForm
          recordsForActions={recordsForActions}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={
          overlayId === EmployeeActionsOverlays.BULK_FLAG_AS_APPLICATION_SENT
        }
        onClose={onClose}
        title={EmployeeActionsOverlays.BULK_FLAG_AS_APPLICATION_SENT}
      >
        <FlagAsApplicationSentForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.BULK_CONFIRM_ENROLLMENT}
        onClose={onClose}
        title={EmployeeActionsOverlays.BULK_CONFIRM_ENROLLMENT}
      >
        <ConfirmEnrollmentForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.WAIVE_COVERAGE}
        onClose={onClose}
        title={EmployeeActionsOverlays.WAIVE_COVERAGE}
      >
        <WaiveCoverageForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Drawer>

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.EDIT_COMPANY_EMAIL}
        onClose={onClose}
        title="Edit company email"
      >
        <EmployeeEmailForm
          onSuccess={onClose}
          onCancel={onClose}
          employeeId={recordsForActions[0]?.employeeId}
        />
      </Drawer>

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.LOG_ENROLLMENT_CALL}
        onClose={onClose}
        title={EmployeeActionsOverlays.LOG_ENROLLMENT_CALL}
      >
        {overlayId === EmployeeActionsOverlays.LOG_ENROLLMENT_CALL &&
          recordsForActions[0]?.onboardingPeriodId && (
            <LogEnrollmentCallForm
              onboardingPeriodId={recordsForActions[0].onboardingPeriodId}
              onClose={onClose}
            />
          )}
      </Drawer>

      <Drawer
        opened={overlayId === EmployeeActionsOverlays.UPDATE_PAYMENT_STATUS}
        onClose={onClose}
        title={EmployeeActionsOverlays.UPDATE_PAYMENT_STATUS}
      >
        {overlayId === EmployeeActionsOverlays.UPDATE_PAYMENT_STATUS &&
          recordsForActions[0]?.benefitId &&
          recordsForActions[0]?.onboardingPeriodId && (
            <ManagePaymentStatusContent
              benefitId={recordsForActions[0].benefitId}
              onboardingPeriodId={recordsForActions[0].onboardingPeriodId}
              onClose={onClose}
            />
          )}
      </Drawer>

      <ResetElectionDialog
        isOpen={overlayId === EmployeeActionsOverlays.RESET_ELECTION}
        recordsForActions={recordsForActions}
        hasNonWaivedRecords={hasNonWaivedRecords}
        onClose={onClose}
        onSuccess={onClose}
      />

      <Modal
        size="md"
        isOpen={overlayId === EmployeeActionsOverlays.CHANGE_SHOPPING_STATUS}
        onClose={onClose}
        title="Change Shopping Status"
      >
        <ChangeShoppingStatusForm
          recordsForActions={recordsForActions}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        onClose={onClose}
        isOpen={overlayId === EmployeeActionsOverlays.DELETE_EMPLOYEE}
        size="55rem"
        title="Confirm deleting employees"
      >
        <DeleteEmployeeForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={resetSelectedEmployeesAndClose}
        />
      </Modal>

      <Modal
        isOpen={overlayId === EmployeeActionsOverlays.EMAIL_ACCOUNT_INVITATION}
        onClose={onClose}
        title={`Send account invitation email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <AccountInvitationForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        isOpen={
          overlayId === EmployeeActionsOverlays.EMAIL_ELECTION_WINDOW_LAUNCH
        }
        onClose={onClose}
        title={`Send election window launch email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <ElectionWindowLaunchForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        isOpen={
          overlayId === EmployeeActionsOverlays.EMAIL_ELECTION_DEADLINE_REMINDER
        }
        onClose={onClose}
        title={`Send election deadline reminder email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <ElectionDeadlineReminderForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        isOpen={overlayId === EmployeeActionsOverlays.EMAIL_ELECTION_SUMMARY}
        onClose={onClose}
        title={`Send election summary email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <ElectionSummaryForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        isOpen={
          overlayId === EmployeeActionsOverlays.EMAIL_ENROLLMENT_CONFIRMATION
        }
        onClose={onClose}
        title={`Send enrollment confirmation email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <EnrollmentConfirmationForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        isOpen={overlayId === EmployeeActionsOverlays.EMAIL_WAIVER_CONFIRMATION}
        onClose={onClose}
        title={`Send waiver confirmation email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <WaiverConfirmationForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>

      <Modal
        isOpen={overlayId === EmployeeActionsOverlays.EMAIL_COBRA_TERMINATION}
        onClose={onClose}
        title={`Send termination email to ${getEmployeeNameOrEmployeesCount(recordsForActions)}?`}
        size="lg"
      >
        <CobraTerminationEmailForm
          recordsForActions={recordsForActions}
          onClose={onClose}
          onSuccess={onClose}
        />
      </Modal>
    </>
  );
};

export { ActionsMenuOverlays };
